class BundleBuilder extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    try {
      this.tiers = JSON.parse(this.dataset.packTiers || '[]');
    } catch (_) {
      this.tiers = [];
    }
    if (this.tiers.length === 0) return;

    this.discountPrefix = this.dataset.discountPrefix || 'Pack-of-';
    this.maxSlots = parseInt(this.dataset.maxSlots, 10) || this.tiers[this.tiers.length - 1].size;

    this.grid = this.querySelector('.bundle-builder__grid');
    this.gridArea = this.querySelector('[data-bundle-grid-area]');
    this.tabsList = this.querySelector('[data-bundle-tabs]');
    this.tabs = Array.from(this.querySelectorAll('[data-bundle-tab]'));
    this.widget = this.querySelector('.bundle-builder__widget');
    this.slots = Array.from(this.querySelectorAll('.bundle-slot[data-slot]'));
    this.tierInputs = Array.from(this.querySelectorAll('.bundle-tier-radio input[type="radio"]'));
    this.toggleBtn = this.querySelector('[data-bundle-toggle]');
    this.prompt = this.querySelector('[data-bundle-prompt]');
    this.atcBtn = this.querySelector('[data-bundle-atc]');
    this.priceWas = this.querySelector('[data-bundle-price-was]');
    this.priceNow = this.querySelector('[data-bundle-price-now]');
    this.savingsEl = this.querySelector('[data-bundle-savings]');
    this.progressFill = this.querySelector('[data-bundle-progress]');
    this.filledCountEl = this.querySelector('[data-bundle-filled]');
    this.totalCountEl = this.querySelector('[data-bundle-total]');
    this.ctaCountEl = this.querySelector('[data-bundle-cta-count]');
    this.ctaTextEl = this.querySelector('[data-bundle-cta-text]');

    this.currentTier = this.tiers[0];

    this.pageSize = parseInt(this.dataset.pageSize, 10) || 75;
    this.viewName = (this.grid && this.grid.dataset.viewName) || 'bundle-cards';
    this.sectionId = this.dataset.sectionId || '';
    this.minimumOrderCents = parseInt(this.dataset.minimumOrder, 10) || 0;
    this.productModal = this.querySelector(`#ProductModal-${this.sectionId}`) || this.querySelector('product-modal');
    this.mediaModalContent = this.querySelector('[data-bundle-media-modal-content]');
    this.mediaModalOpener = this.querySelector('[data-bundle-media-opener]');
    this.mediaModalCache = new Map();

    this.syncPaginationRefs();
    this.bindEvents();
    this.applyTier(this.currentTier);
    this.render();
    this.setupPagination();
    if (this.grid && this.getLoadedCount() === 0 && (this.hasMore || this.currentPage < this.totalPages)) {
      this.loadCollectionPage(this.grid.dataset.collectionHandle, this.currentPage + 1, false);
    }
    this.setupInitialWidgetState();
    this.initSliders();
  }

  initSliders(scope) {
    const root = scope || this.grid;
    if (!root) return;
    const sliders = root.querySelectorAll('[data-bundle-slider]');
    if (sliders.length === 0) return;
    if (typeof window.Swiper === 'undefined') {
      if (!this._swiperWaitScheduled) {
        this._swiperWaitScheduled = true;
        const waitFor = () => {
          if (typeof window.Swiper !== 'undefined') {
            this._swiperWaitScheduled = false;
            this.initSliders();
          } else {
            setTimeout(waitFor, 80);
          }
        };
        waitFor();
      }
      return;
    }
    sliders.forEach((slider) => this.setupSlider(slider));
  }

  setupSlider(slider) {
    if (slider.dataset.sliderReady === '1') return;
    const slides = slider.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) {
      slider.dataset.sliderReady = '1';
      return;
    }
    const pagination = slider.querySelector('.swiper-pagination');
    slider._swiperInstance = new window.Swiper(slider, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      grabCursor: false,
      speed: 350,
      threshold: 4,
      resistanceRatio: 0.6,
      pagination: pagination ? {
        el: pagination,
        clickable: true,
        bulletClass: 'bundle-product-card__dot',
        bulletActiveClass: 'is-active'
      } : false
    });
    slider.dataset.sliderReady = '1';
  }

  setupInitialWidgetState() {
    if (!this.widget) return;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
    if (!isMobile) return;
    this.widget.setAttribute('data-state', 'collapsed');
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
  }

  setupPagination() {
    if (!this.grid) return;
    const handle = this.grid.dataset.collectionHandle;
    if (!handle) return;

    this.collectionHandle = handle;
    this.viewName = this.grid.dataset.viewName || 'bundle-cards';
    this.currentPage = parseInt(this.grid.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.grid.dataset.totalPages, 10) || 1;
    this.collectionCount = parseInt(this.grid.dataset.collectionCount, 10) || 0;
    this.hasMore = this.grid.dataset.hasMore === 'true';
    this.isLoadingPage = false;

    this.syncTotalPages();
    this.updateShowMoreButton();
  }

  syncTotalPages() {
    if (this.collectionCount > 0 && this.pageSize > 0) {
      this.totalPages = Math.max(this.totalPages, Math.ceil(this.collectionCount / this.pageSize));
    }
    if (this.hasMore && this.currentPage >= this.totalPages) {
      this.totalPages = this.currentPage + 1;
    }
  }

  syncPaginationRefs() {
    this.grid = this.gridArea ? this.gridArea.querySelector('.bundle-builder__grid') : this.querySelector('.bundle-builder__grid');
    this.loadMoreWrap = this.querySelector('[data-bundle-load-more]');
    this.showMoreBtn = this.querySelector('[data-bundle-show-more]');
    this.loadingOverlay = this.querySelector('[data-bundle-loading]');
    this.loadMoreSpinner = this.loadMoreWrap ? this.loadMoreWrap.querySelector('[data-bundle-spinner]') : null;
  }

  setLoadingState(isLoading, fullReplace = false) {
    this.isLoadingPage = isLoading;

    if (this.gridArea) {
      this.gridArea.classList.toggle('is-loading', isLoading);
    }

    if (this.loadingOverlay) {
      this.loadingOverlay.hidden = !isLoading || !fullReplace;
      this.loadingOverlay.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (this.loadMoreSpinner) {
      this.loadMoreSpinner.hidden = !isLoading || fullReplace;
    }

    if (this.showMoreBtn) {
      this.showMoreBtn.disabled = isLoading || !this.canLoadMore();
      this.showMoreBtn.classList.toggle('is-loading', isLoading && !fullReplace);
    }
  }

  getLoadedCount() {
    if (!this.grid) return 0;
    return this.grid.querySelectorAll('li.grid__item').length;
  }

  canLoadMore() {
    if (this.isLoadingPage) return false;
    if (!this.grid || this.getLoadedCount() === 0) return false;
    if (this.hasMore) return true;
    if (this.currentPage < this.totalPages) return true;
    return false;
  }

  updateShowMoreButton() {
    if (!this.loadMoreWrap) return;
    const show = this.canLoadMore();
    this.loadMoreWrap.hidden = !show;
    if (this.showMoreBtn && !this.isLoadingPage) {
      this.showMoreBtn.disabled = !show;
    }
  }

  parseCollectionResponse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const meta = doc.querySelector('[data-bundle-page-meta]');
    const items = doc.querySelectorAll('li.grid__item');
    const pageSize = this.pageSize || 75;
    const currentPage = meta ? parseInt(meta.dataset.currentPage, 10) || 1 : 1;
    let collectionCount = meta ? parseInt(meta.dataset.collectionCount, 10) || 0 : 0;
    let totalPages = meta ? parseInt(meta.dataset.totalPages, 10) || 1 : 1;
    let hasMore = meta ? meta.dataset.hasMore === 'true' : false;

    if (collectionCount > 0) {
      totalPages = Math.max(totalPages, Math.ceil(collectionCount / pageSize));
      if (currentPage * pageSize < collectionCount) {
        hasMore = true;
      }
    }

    if (items.length >= pageSize && currentPage < totalPages) {
      hasMore = true;
    }

    if (!meta && items.length >= pageSize) {
      hasMore = true;
      totalPages = Math.max(totalPages, currentPage + 1);
    }

    return { items, currentPage, totalPages, collectionCount, hasMore };
  }

  appendGridItems(items) {
    if (!this.grid || items.length === 0) return [];

    const itemsToAdd = Array.from(items);
    const frag = document.createDocumentFragment();
    itemsToAdd.forEach((li) => frag.appendChild(li));
    this.grid.appendChild(frag);
    return itemsToAdd;
  }

  async fetchPageWithItems(handle, startPage) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let page = startPage;
    let result = null;
    for (let i = 0; i < 200; i++) {
      const url = `${root}collections/${handle}?view=${this.viewName || 'bundle-cards'}&page=${page}`;
      const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      result = this.parseCollectionResponse(await res.text());
      if (result.items.length > 0) return result;
      if (!result.hasMore || page >= result.totalPages) return result;
      page += 1;
    }
    return result;
  }

  async loadCollectionPage(handle, page, replace = false) {
    if (this.isLoadingPage) return;
    if (!handle) return;

    this.setLoadingState(true, replace);

    try {
      const { items, currentPage, totalPages, collectionCount, hasMore } = await this.fetchPageWithItems(handle, page);

      if (replace) {
        this.ensureGridStructure();
        if (this.grid) this.grid.innerHTML = '';
      }

      if (items.length === 0 && replace) {
        this.hasMore = false;
        this.showEmptyState();
      } else if (items.length === 0) {
        this.currentPage = this.totalPages;
        this.hasMore = false;
      } else {
        this.hideEmptyState();
        const addedItems = this.appendGridItems(items);
        this.refreshAddedCards();
        addedItems.forEach((li) => this.initSliders(li));
      }

      this.collectionHandle = handle;
      if (this.grid) {
        this.grid.dataset.collectionHandle = handle;
        this.grid.dataset.currentPage = String(currentPage);
        this.grid.dataset.totalPages = String(totalPages);
        this.grid.dataset.hasMore = hasMore ? 'true' : 'false';
        if (collectionCount > 0) {
          this.grid.dataset.collectionCount = String(collectionCount);
          this.collectionCount = collectionCount;
        }
      }
      this.currentPage = currentPage;
      this.totalPages = totalPages;
      this.hasMore = hasMore;
      this.syncTotalPages();

      this.syncPaginationRefs();
    } catch (err) {
      console.error('[bundle-builder] collection load failed', err);
      if (replace) {
        this.showEmptyState(true);
      }
    } finally {
      this.setLoadingState(false, replace);
      this.updateShowMoreButton();
    }
  }

  ensureGridStructure() {
    if (!this.gridArea) return;

    if (!this.gridArea.querySelector('[data-bundle-loading]')) {
      const loading = document.createElement('div');
      loading.className = 'bundle-builder__loading';
      loading.setAttribute('data-bundle-loading', '');
      loading.setAttribute('aria-live', 'polite');
      loading.setAttribute('aria-busy', 'false');
      loading.hidden = true;
      loading.innerHTML = `
        <span class="bundle-builder__spinner" aria-hidden="true"></span>
        <span class="bundle-builder__loading-text">Loading products…</span>
      `;
      this.gridArea.prepend(loading);
    }

    let grid = this.gridArea.querySelector('.bundle-builder__grid');
    if (!grid) {
      grid = document.createElement('ul');
      grid.className = 'bundle-builder__grid product-grid grid grid--rigid grid--2-col grid--2-col-tablet grid--3-col-desktop';
      grid.setAttribute('role', 'list');
      grid.dataset.viewName = 'bundle-cards';
      grid.dataset.pageSize = String(this.pageSize);
      this.gridArea.appendChild(grid);
    }

    if(this.querySelector("load-more-search")) this.querySelector("load-more-search").remove();
    let loadMoreWrap = this.gridArea.querySelector('[data-bundle-load-more]');
    if (!loadMoreWrap) {
      loadMoreWrap = document.createElement('div');
      loadMoreWrap.className = 'bundle-builder__load-more';
      loadMoreWrap.setAttribute('data-bundle-load-more', '');
      loadMoreWrap.innerHTML = `
        <button type="button" class="image-with-text-grid__button bundle-builder__show-more" data-bundle-show-more>
          Show More
        </button>
        <span class="bundle-builder__spinner" data-bundle-spinner hidden aria-hidden="true"></span>
      `;
      this.gridArea.appendChild(loadMoreWrap);
    }

    this.grid = grid;
    this.syncPaginationRefs();
  }

  showEmptyState(isError = false) {
    if (!this.gridArea) return;
    let empty = this.gridArea.querySelector('[data-bundle-empty]');
    if (!empty) {
      empty = document.createElement('div');
      empty.className = 'bundle-builder__empty';
      empty.setAttribute('data-bundle-empty', '');
      this.gridArea.appendChild(empty);
    }
    empty.innerHTML = isError
      ? '<p>Unable to load products. Please try again.</p>'
      : '<p>No products found in this collection.</p>';
    empty.hidden = false;
    if (this.grid) this.grid.hidden = true;
    if (this.loadMoreWrap) this.loadMoreWrap.hidden = true;
  }

  hideEmptyState() {
    if (!this.gridArea) return;
    const empty = this.gridArea.querySelector('[data-bundle-empty]');
    if (empty) empty.hidden = true;
    if (this.grid) this.grid.hidden = false;
  }

  switchTab(tabBtn) {
    if (!tabBtn || tabBtn.classList.contains('is-active')) return;
    const handle = tabBtn.dataset.collectionHandle;
    if (!handle) return;

    this.tabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
      tab.tabIndex = -1;
    });
    tabBtn.classList.add('is-active');
    tabBtn.setAttribute('aria-selected', 'true');
    tabBtn.tabIndex = 0;

    this.scrollTabIntoView(tabBtn);
    this.loadCollectionPage(handle, 1, true);
  }

  scrollTabIntoView(tabBtn) {
    if (!this.tabsList || !tabBtn) return;
    const listRect = this.tabsList.getBoundingClientRect();
    const tabRect = tabBtn.getBoundingClientRect();
    const offset = tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2;
    this.tabsList.scrollBy({ left: offset, behavior: 'smooth' });
  }

  onTabsPointerDown(event) {
    if (event.button !== 0 || !event.isPrimary || !this.tabsList) return;

    this.tabsDragList = this.tabsList;
    this.tabsDragPointerId = event.pointerId;
    this.tabsDragStartX = event.pageX;
    this.tabsDragStartScrollLeft = this.tabsList.scrollLeft;
    this.tabsHasDragged = false;
    this.tabsHasPointerCapture = false;

    window.addEventListener('pointermove', this.onTabsPointerMove);
    window.addEventListener('pointerup', this.onTabsPointerUp);
    window.addEventListener('pointercancel', this.onTabsPointerUp);
  }

  onTabsPointerMove(event) {
    if (!this.tabsDragList || event.pointerId !== this.tabsDragPointerId) return;

    const dragDistance = event.pageX - this.tabsDragStartX;

    if (Math.abs(dragDistance) > 5) {
      this.tabsHasDragged = true;
      this.tabsDragList.classList.add('is-dragging');

      if (!this.tabsHasPointerCapture && this.tabsDragList.setPointerCapture) {
        this.tabsDragList.setPointerCapture(event.pointerId);
        this.tabsHasPointerCapture = true;
      }
    }

    if (this.tabsHasDragged) {
      event.preventDefault();
      this.tabsDragList.scrollLeft = this.tabsDragStartScrollLeft - dragDistance;
    }
  }

  onTabsPointerUp(event) {
    if (!this.tabsDragList || event.pointerId !== this.tabsDragPointerId) return;

    const tabsList = this.tabsDragList;

    if (this.tabsHasDragged) {
      tabsList.dataset.dragged = 'true';
      window.setTimeout(() => {
        tabsList.removeAttribute('data-dragged');
      }, 0);
    }

    tabsList.classList.remove('is-dragging');
    if (this.tabsHasPointerCapture && tabsList.hasPointerCapture(event.pointerId)) {
      tabsList.releasePointerCapture(event.pointerId);
    }

    this.tabsDragList = null;
    this.tabsDragPointerId = null;
    this.tabsHasDragged = false;
    this.tabsHasPointerCapture = false;

    window.removeEventListener('pointermove', this.onTabsPointerMove);
    window.removeEventListener('pointerup', this.onTabsPointerUp);
    window.removeEventListener('pointercancel', this.onTabsPointerUp);
  }

  async loadNextPage() {
    if (!this.canLoadMore()) return;

    this.setLoadingState(true, false);
    const nextPage = this.currentPage + 1;

    try {
      const { items, currentPage, totalPages, collectionCount, hasMore } = await this.fetchPageWithItems(this.collectionHandle, nextPage);

      if (items.length === 0) {
        this.currentPage = this.totalPages;
        this.hasMore = false;
      } else {
        const addedItems = this.appendGridItems(items);
        this.currentPage = currentPage;
        this.totalPages = totalPages;
        if (this.grid) {
          this.grid.dataset.currentPage = String(currentPage);
          this.grid.dataset.totalPages = String(totalPages);
          this.grid.dataset.hasMore = hasMore ? 'true' : 'false';
          if (collectionCount > 0) {
            this.grid.dataset.collectionCount = String(collectionCount);
            this.collectionCount = collectionCount;
          }
        }
        this.currentPage = currentPage;
        this.totalPages = totalPages;
        this.hasMore = hasMore;
        this.syncTotalPages();
        this.refreshAddedCards();
        addedItems.forEach((li) => this.initSliders(li));
      }
    } catch (err) {
      console.error('[bundle-builder] page load failed', err);
    } finally {
      this.setLoadingState(false, false);
      this.updateShowMoreButton();
    }
  }

  refreshAddedCards() {
    if (!this.grid) return;
    this.grid.querySelectorAll('.bundle-product-card').forEach((card) => this.updateCardState(card));
  }

  getMediaIdFromCard(card, target) {
    if (!card) return '';
    const slide = target?.closest('[data-media-id]');
    if (slide?.dataset.mediaId) return slide.dataset.mediaId;
    const first = card.querySelector('[data-media-id]');
    return first?.dataset.mediaId || '';
  }

  setCardMediaLoading(card, isLoading) {
    if (this.mediaLoadingCard && this.mediaLoadingCard !== card) {
      this.setCardMediaLoading(this.mediaLoadingCard, false);
    }

    const media = card?.querySelector('.bundle-product-card__media');
    if (!media) return;

    media.classList.toggle('is-loading', isLoading);
    media.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    const loader = media.querySelector('[data-bundle-media-loader]');
    if (loader) {
      loader.hidden = !isLoading;
      loader.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
    }

    this.mediaLoadingCard = isLoading ? card : null;
  }

  showMediaModalContent(html, mediaId) {
    this.mediaModalContent.innerHTML = html;
    const targetId = mediaId || this.mediaModalContent.querySelector('[data-media-id]')?.dataset.mediaId;
    if (!targetId) return false;

    this.mediaModalOpener.setAttribute('data-media-id', targetId);
    this.productModal.show(this.mediaModalOpener);
    return true;
  }

  async openProductMediaModal(card, mediaId) {
    if (this.mediaModalLoading || !this.productModal || !this.mediaModalContent || !this.mediaModalOpener) return;

    const handle = card?.dataset.productHandle;
    if (!handle) return;

    const cached = this.mediaModalCache.get(handle);
    if (cached) {
      this.showMediaModalContent(cached, mediaId);
      return;
    }

    this.mediaModalLoading = true;
    this.setCardMediaLoading(card, true);

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}products/${handle}?view=bundle-media`, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = doc.querySelector('[data-bundle-media-content]');
      if (!content) throw new Error('Modal content missing');
      const html = content.innerHTML;
      this.mediaModalCache.set(handle, html);
      this.showMediaModalContent(html, mediaId);
    } catch (err) {
      console.error('[bundle-builder] media modal failed', err);
    } finally {
      this.setCardMediaLoading(card, false);
      this.mediaModalLoading = false;
    }
  }

  bindEvents() {
    this.onTabsPointerMove = this.onTabsPointerMove.bind(this);
    this.onTabsPointerUp = this.onTabsPointerUp.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        if (this.tabsList && this.tabsList.dataset.dragged === 'true') {
          event.preventDefault();
          return;
        }
        this.switchTab(tab);
      });
    });

    if (this.tabsList) {
      this.tabsList.addEventListener('pointerdown', (event) => this.onTabsPointerDown(event));
      this.tabsList.addEventListener('dragstart', (event) => event.preventDefault());
    }

    const gridEventRoot = this.gridArea || this.grid;
    if (gridEventRoot) {
      gridEventRoot.addEventListener('click', (event) => {
        const showMoreBtn = event.target.closest('[data-bundle-show-more]');
        if (showMoreBtn) {
          event.preventDefault();
          if (!showMoreBtn.disabled) this.loadNextPage();
          return;
        }

        if (event.target.closest('.swiper-pagination, .bundle-product-card__dot, [data-bundle-dot]')) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const addBtn = event.target.closest('[data-bundle-add]');
        if (addBtn) {
          if (addBtn.disabled) return;
          event.preventDefault();
          const card = addBtn.closest('.bundle-product-card');
          if (card) this.addProduct(card);
          return;
        }
        const imageLink = event.target.closest('.bundle-product-card__media');
        if (imageLink) {
          event.preventDefault();
          event.stopPropagation();
          const card = imageLink.closest('.bundle-product-card');
          if (!card) return;
          const mediaId = this.getMediaIdFromCard(card, event.target);
          this.openProductMediaModal(card, mediaId);
          return;
        }
      });

      gridEventRoot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const mediaEl = event.target.closest('.bundle-product-card__media');
        if (!mediaEl) return;
        event.preventDefault();
        const card = mediaEl.closest('.bundle-product-card');
        const mediaId = this.getMediaIdFromCard(card, event.target);
        this.openProductMediaModal(card, mediaId);
      });

      gridEventRoot.addEventListener('change', (event) => {
        const variantEl = event.target.closest('[data-bundle-variant]');
        if (variantEl) {
          this.syncCardPrice(variantEl);
          this.syncAddButton(variantEl);
        }
      });
    }

    this.tierInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const size = parseInt(input.dataset.pack, 10);
        const discount = parseInt(input.dataset.discount, 10);
        const code = input.dataset.code || '';
        this.currentTier = { size, discount, code };
        this.applyTier(this.currentTier);
        this.render();
      });
    });

    this.slots.forEach((slot) => {
      const removeBtn = slot.querySelector('[data-bundle-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.removeFromSlot(slot);
        });
      }
    });

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggleWidget());
    }

    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.checkout();
      });
    }
  }

  applyTier(tier) {
    this.slots.forEach((slot) => {
      const idx = parseInt(slot.dataset.slot, 10);
      if (idx <= tier.size) {
        slot.classList.add('is-active');
      } else {
        slot.classList.remove('is-active');
        if (!slot.classList.contains('is-empty')) {
          const productId = slot.dataset.productId;
          this.emptySlot(slot);
          if (productId) this.updateCardStateByProductId(productId);
        }
      }
    });
  }

  addProduct(card) {
    const variantEl = card.querySelector('[data-bundle-variant]');
    if (!variantEl) return;

    const variant = this.readVariant(variantEl);
    if (!variant.id) return;

    const variantQty = this.getVariantQtyInBundle(variant.id);
    const maxQty = this.getVariantMaxQty(variant);
    if (variantQty >= maxQty) {
      this.flashCardLimit(card);
      return;
    }

    let target = this.firstEmptyActiveSlot();
    if (!target) {
      const nextTierIndex = this.tiers.findIndex((t) => t.size === this.currentTier.size) + 1;
      if (nextTierIndex > 0 && nextTierIndex < this.tiers.length) {
        const nextTier = this.tiers[nextTierIndex];
        const nextInput = this.tierInputs.find((i) => parseInt(i.dataset.pack, 10) === nextTier.size);
        if (nextInput) {
          nextInput.checked = true;
          this.currentTier = nextTier;
          this.applyTier(this.currentTier);
          target = this.firstEmptyActiveSlot();
        }
      }
    }
    if (!target) return;

    this.fillSlot(target, card, variant);
    this.updateCardState(card);
    this.render();
  }

  fillSlot(slot, card, variant) {
    const imgSrc = this.getCardImageSrc(card);
    const productTitle = card.dataset.productTitle || '';
    const productId = card.dataset.productId || '';

    slot.dataset.variantId = variant.id;
    slot.dataset.variantPriceRaw = variant.priceRaw;
    slot.dataset.productId = productId;

    const content = slot.querySelector('[data-bundle-slot-content]');
    if (!content) return;

    content.innerHTML = '';

    if (imgSrc) {
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = productTitle;
      img.loading = 'lazy';
      img.className = 'bundle-slot__image';
      content.appendChild(img);
    }

    const meta = document.createElement('div');
    meta.className = 'bundle-slot__meta';

    const title = document.createElement('p');
    title.className = 'bundle-slot__title';
    title.textContent = productTitle;
    meta.appendChild(title);

    if (variant.title && variant.title.toLowerCase() !== 'default title') {
      const variantText = document.createElement('p');
      variantText.className = 'bundle-slot__variant';
      variantText.textContent = variant.title;
      meta.appendChild(variantText);
    }

    const priceText = document.createElement('p');
    priceText.className = 'bundle-slot__price';
    priceText.textContent = variant.price;
    meta.appendChild(priceText);

    content.appendChild(meta);

    slot.classList.remove('is-empty');
    slot.classList.add('is-filled');
  }

  emptySlot(slot) {
    delete slot.dataset.variantId;
    delete slot.dataset.variantPriceRaw;
    delete slot.dataset.productId;
    const content = slot.querySelector('[data-bundle-slot-content]');
    if (content) content.innerHTML = '';
    slot.classList.remove('is-filled');
    slot.classList.add('is-empty');
  }

  removeFromSlot(slot) {
    if (slot.classList.contains('is-empty')) return;

    const productId = slot.dataset.productId;
    this.emptySlot(slot);
    if (productId) this.updateCardStateByProductId(productId);
    this.compactSlots();
    this.render();
  }

  compactSlots() {
    const activeSlots = this.slots.filter((s) => s.classList.contains('is-active'));
    for (let i = 0; i < activeSlots.length - 1; i++) {
      const here = activeSlots[i];
      if (here.classList.contains('is-empty')) {
        for (let j = i + 1; j < activeSlots.length; j++) {
          const next = activeSlots[j];
          if (!next.classList.contains('is-empty')) {
            this.moveSlotData(next, here);
            break;
          }
        }
      }
    }
  }

  moveSlotData(from, to) {
    const variantId = from.dataset.variantId;
    const priceRaw = from.dataset.variantPriceRaw;
    const productId = from.dataset.productId;
    const content = from.querySelector('[data-bundle-slot-content]');
    const targetContent = to.querySelector('[data-bundle-slot-content]');

    to.dataset.variantId = variantId;
    to.dataset.variantPriceRaw = priceRaw;
    to.dataset.productId = productId;
    if (targetContent && content) targetContent.innerHTML = content.innerHTML;
    to.classList.remove('is-empty');
    to.classList.add('is-filled');

    this.emptySlot(from);
  }

  syncCardPrice(variantEl) {
    const variant = this.readVariant(variantEl);
    const card = variantEl.closest('.bundle-product-card');
    if (!card || !variant.price) return;
    const priceLabel = card.querySelector('[data-bundle-card-price]');
    if (priceLabel) priceLabel.textContent = variant.price;
  }

  syncAddButton(variantEl) {
    const card = variantEl.closest('.bundle-product-card');
    if (card) this.updateAddButtonState(card);
  }

  flashSelect(selectEl) {
    if (!selectEl) return;
    const wrapper = selectEl.closest('.bundle-product-card__select') || selectEl;
    wrapper.classList.add('needs-size');
    selectEl.focus();
    setTimeout(() => wrapper.classList.remove('needs-size'), 900);
  }

  readVariant(variantEl) {
    let option;
    if (variantEl.tagName === 'SELECT') {
      option = variantEl.options[variantEl.selectedIndex];
    } else {
      option = variantEl;
    }
    if (!option) return {};
    const maxQtyRaw = option.getAttribute('data-max-qty');
    return {
      id: variantEl.tagName === 'SELECT' ? option.value : variantEl.value,
      price: option.getAttribute('data-price') || '',
      priceRaw: parseInt(option.getAttribute('data-price-raw') || '0', 10),
      title: option.getAttribute('data-title') || '',
      maxQty: maxQtyRaw != null ? parseInt(maxQtyRaw, 10) : 2
    };
  }

  getVariantMaxQty(variant) {
    const maxQty = parseInt(variant?.maxQty, 10);
    if (Number.isFinite(maxQty) && maxQty >= 0) return maxQty;
    return 2;
  }

  getVariantQtyInBundle(variantId) {
    if (!variantId) return 0;
    const id = String(variantId);
    return this.slots.filter((slot) => slot.classList.contains('is-filled') && slot.dataset.variantId === id).length;
  }

  getProductQtyInBundle(productId) {
    if (!productId) return 0;
    return this.slots.filter((slot) => slot.classList.contains('is-filled') && slot.dataset.productId === productId).length;
  }

  updateCardStateByProductId(productId) {
    if (!this.grid || !productId) return;
    const card = this.grid.querySelector(`.bundle-product-card[data-product-id="${productId}"]`);
    if (card) this.updateCardState(card);
  }

  getCardImageSrc(card) {
    const img = card.querySelector('.bundle-product-card__image, img');
    if (!img) return '';
    return img.currentSrc || img.src || '';
  }

  firstEmptyActiveSlot() {
    return this.slots.find((s) => s.classList.contains('is-active') && s.classList.contains('is-empty')) || null;
  }

  updateCardState(card) {
    if (!card) return;
    const productId = card.dataset.productId;
    const productQty = this.getProductQtyInBundle(productId);
    if (productQty > 0) {
      card.setAttribute('data-qty', `Qty: ${productQty}`);
      card.classList.add('is-in-bundle');
    } else {
      card.removeAttribute('data-qty');
      card.classList.remove('is-in-bundle');
    }
    this.updateAddButtonState(card);
  }

  updateAddButtonState(card) {
    if (!card) return;
    const addBtn = card.querySelector('[data-bundle-add]');
    if (!addBtn || card.classList.contains('bundle-product-card--soldout')) return;
    const variantEl = card.querySelector('[data-bundle-variant]');
    const hasVariant = variantEl ? !!variantEl.value : false;
    const variant = variantEl ? this.readVariant(variantEl) : {};
    const variantQty = variant.id ? this.getVariantQtyInBundle(variant.id) : 0;
    const maxQty = this.getVariantMaxQty(variant);
    const atLimit = hasVariant && variantQty >= maxQty;
    const disabled = !hasVariant || atLimit;
    addBtn.disabled = disabled;
    addBtn.setAttribute('aria-disabled', String(disabled));
    card.classList.toggle('is-at-limit', atLimit);
  }

  flashCardLimit(card) {
    if (!card) return;
    card.classList.remove('shake-limit');
    void card.offsetWidth;
    card.classList.add('shake-limit');
    setTimeout(() => card.classList.remove('shake-limit'), 500);
  }

  render() {
    const tier = this.currentTier;
    const active = this.slots.filter((s) => s.classList.contains('is-active'));
    const filled = active.filter((s) => s.classList.contains('is-filled'));

    if (this.filledCountEl) this.filledCountEl.textContent = filled.length;
    if (this.totalCountEl) this.totalCountEl.textContent = tier.size;

    const progressTrack = this.querySelector('.bundle-builder__progress');
    if (progressTrack) {
      const pct = active.length === 0 ? 0 : Math.round((filled.length / active.length) * 100);
      progressTrack.style.setProperty('--bundle-progress-pct', `${pct}%`);
    }

    const totalRaw = filled.reduce((sum, s) => sum + (parseInt(s.dataset.variantPriceRaw, 10) || 0), 0);
    const discounted = Math.round(totalRaw * (1 - tier.discount / 100));

    const fmt = (cents) => this.formatMoney(cents);

    if (this.priceWas) {
      this.priceWas.textContent = filled.length > 0 ? fmt(totalRaw) : '';
    }
    if (this.priceNow) {
      this.priceNow.textContent = fmt(filled.length > 0 ? discounted : 0);
    }
    if (this.savingsEl) this.savingsEl.textContent = tier.discount;

    const remaining = active.length - filled.length;
    const meetsMinimum = !this.minimumOrderCents || totalRaw >= this.minimumOrderCents;
    const bundleComplete = remaining === 0 && active.length > 0;

    if (this.atcBtn) {
      if (bundleComplete && meetsMinimum) {
        this.atcBtn.disabled = false;
        this.atcBtn.classList.add('is-ready');
        if (this.ctaTextEl) this.ctaTextEl.textContent = 'PROCEED TO CHECKOUT';
      } else if (bundleComplete && !meetsMinimum) {
        this.atcBtn.disabled = true;
        this.atcBtn.classList.remove('is-ready');
        if (this.ctaTextEl) {
          this.ctaTextEl.textContent = `MINIMUM ORDER ${fmt(this.minimumOrderCents)}`;
        }
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.classList.remove('is-ready');
        if (this.ctaTextEl) {
          this.ctaTextEl.innerHTML = `ADD <span data-bundle-cta-count>${remaining}</span> MORE`;
        }
      }
    }

    if (this.prompt) {
      const showPrompt = remaining === 0 && filled.length < this.maxSlots && this.canUpgradeTier();
      this.prompt.hidden = !showPrompt;
    }
  }

  canUpgradeTier() {
    const idx = this.tiers.findIndex((t) => t.size === this.currentTier.size);
    return idx >= 0 && idx < this.tiers.length - 1;
  }

  toggleWidget() {
    if (!this.widget) return;
    const expanded = this.widget.getAttribute('data-state') === 'expanded';
    this.widget.setAttribute('data-state', expanded ? 'collapsed' : 'expanded');
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('aria-expanded', String(!expanded));
    }
  }

  formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.shopSettings) {
      return window.theme.Currency.formatMoney(cents, window.theme.shopSettings.moneyFormat);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  getBundleTrackingAttributes() {
    const tier = this.currentTier || {};
    const packSize = tier.size ? `${tier.size}-Pack` : '';
    const discount = tier.discount ? `${tier.discount}% off` : '';
    const code = (tier.code || '').trim();

    const attributes = {
      'Bundle Builder': 'Yes'
    };

    if (packSize) attributes['Bundle Pack Size'] = packSize;
    if (discount) attributes['Bundle Discount'] = discount;
    if (code) attributes['Bundle Discount Code'] = code;

    return attributes;
  }

  async checkout() {
    const filled = this.slots.filter((s) => s.classList.contains('is-active') && s.classList.contains('is-filled'));
    if (filled.length === 0) return;

    const totalRaw = filled.reduce((sum, s) => sum + (parseInt(s.dataset.variantPriceRaw, 10) || 0), 0);
    if (this.minimumOrderCents > 0 && totalRaw < this.minimumOrderCents) return;

    const counts = new Map();
    filled.forEach((s) => {
      const id = s.dataset.variantId;
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });

    const items = Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));

    const originalLabel = this.ctaTextEl ? this.ctaTextEl.innerHTML : '';
    if (this.ctaTextEl) this.ctaTextEl.textContent = 'PROCESSING…';
    this.atcBtn.disabled = true;

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
      await res.json();

      const attrRes = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: this.getBundleTrackingAttributes() })
      });
      if (!attrRes.ok) throw new Error(`Cart update failed: ${attrRes.status}`);

      const code = (this.currentTier.code || '').trim();
      window.location.href = code
        ? `/checkout?discount=${encodeURIComponent(code)}`
        : '/checkout';
    } catch (err) {
      console.error('[bundle-builder] checkout error', err);
      if (this.ctaTextEl) this.ctaTextEl.innerHTML = originalLabel || 'TRY AGAIN';
      this.atcBtn.disabled = false;
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}


class BundleBuilderSearch extends HTMLElement {
  constructor() {
    super();
    this._debounceTimer = null;
    this._debounceDelay = 350; // ms - tweak as needed
  }
  connectedCallback() {
    console.log("...Bundle Builder search initilize")
    this._form = this.querySelector("form");
    this._input = this.querySelector("input");
    this._bundle = document.querySelector("bundle-builder");
    this._loading = this._bundle?.querySelector(".bundle-builder__loading")

    this._input.addEventListener("input", (event) => {
      const value = event.target.value.trim();

      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = null;
      }

      if (value.length === 0) {
        this.resetToActiveTab();
        return;
      }

      this._debounceTimer = setTimeout(() => {
        this.search(value);
      }, this._debounceDelay);
    })

    this._input.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;

      event.preventDefault();

      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = null;
      }

      const value = this._input.value.trim();

      if (value.length === 0) {
        this.resetToActiveTab();
      } else {
        this.search(value);
      }
    })

    this._form.addEventListener("submit", (event) => {
      event.preventDefault();
      event.stopPropagation();
    })
  }

  resetToActiveTab() {
    const activeTab = this._bundle.querySelector(".bundle-builder__tab.is-active");
    if (!activeTab) return;

    activeTab.classList.remove("is-active");
    activeTab.click();
  }

  search(q) {
    const rootRoute = window.Shopify?.routes?.root || '/';
    let activeCollectionHandle = this._bundle.querySelector(".bundle-builder__tab.is-active")?.dataset.collectionHandle;
    const Tab = this._bundle.querySelectorAll(".bundle-builder__tab");
    if(Tab.length > 0) {
      Tab.forEach(tab => tab.classList.remove("is-active"));
      Tab[0].classList.add("is-active")
    }
    // /search (not /search/suggest) — supports filter.p.collection AND
    // full result counts via the paginate tag in Liquid (predictive search
    // hard-caps at 10 and ignores collection filters entirely).
    const requestUrl = `${rootRoute}search?q=${encodeURIComponent(q)}&type=product&filter.p.collection=${encodeURIComponent(activeCollectionHandle)}&sections=bundle-builder-api`;
    console.log(requestUrl, "requestUrl")
    this._loading.removeAttribute('hidden');
    fetch(requestUrl)
      .then(response => response.json())
      .then(data => {
        console.log("data", data)

        const sectionHtml = data?.["bundle-builder-api"];
        if (!sectionHtml) {
          console.warn("No bundle-builder-api section returned in response", data);
          return;
        }
this._loading.setAttribute('hidden' , 'true');
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newGrid = doc.querySelector('.bundle-builder__grid');
        const currentGrid = this._bundle.querySelector('.bundle-builder__grid');
        const newLoadMore = doc.querySelector('load-more-search');
        console.log("newGrid" , newGrid , doc)
         let show_more_button = this._bundle.querySelector(".bundle-builder__load-more");
          if (show_more_button) show_more_button.remove();
          if(this._bundle.querySelector("load-more-search")) this._bundle.querySelector("load-more-search").remove();
        if (newGrid && currentGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
          if (this._bundle && typeof this._bundle.initSliders === 'function') {
            this._bundle.initSliders();
          }
          if(newLoadMore) {
             currentGrid.insertAdjacentElement('afterend', newLoadMore);
          }
         
         
        } else if(currentGrid){
            currentGrid.innerHTML = `<p class="" style="text-align:center;"> No product found </p>`;
          console.warn("Missing .bundle-builder__grid in response or current DOM");
        }
      })
      .catch(err => {
        console.error("Bundle builder search request failed:", err);
      })
  }
}
if (!customElements.get('bundle-builder-search')) {
  customElements.define('bundle-builder-search', BundleBuilderSearch)
}


class LoadMoreSearch extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this._bundle = document.querySelector("bundle-builder");
    this._isLoading = false;

    this.addEventListener("click", () => this.loadNextPage());
  }

  loadNextPage() {
    const nextUrl = this.dataset.nextUrl;
    if (!nextUrl || this._isLoading) return;

    this._isLoading = true;
    this.disabled = true;
    this.classList.add("is-loading");

    const requestUrl = `${nextUrl}&sections=bundle-builder-api`;

    fetch(requestUrl)
      .then(response => response.json())
      .then(data => this.renderNextPage(data))
      .catch(err => console.error("Load more search failed:", err))
      .finally(() => {
        this._isLoading = false;
        this.disabled = false;
        this.classList.remove("is-loading");
      });
  }

  renderNextPage(data) {
    const sectionHtml = data?.["bundle-builder-api"];
    if (!sectionHtml) {
      console.warn("No bundle-builder-api section returned in response", data);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newGrid = doc.querySelector('.bundle-builder__grid');
    const currentGrid = this._bundle?.querySelector('.bundle-builder__grid');
    const meta = doc.querySelector('[data-total-results]');

    if (newGrid && currentGrid) {
      currentGrid.insertAdjacentHTML('beforeend', newGrid.innerHTML);

      if (this._bundle && typeof this._bundle.initSliders === 'function') {
        this._bundle.initSliders();
      }
    } else {
      console.warn("Missing .bundle-builder__grid in response or current DOM");
    }

    // Update own state from the new meta so the next click moves forward another page
    if (meta) {
      this.dataset.totalResults = meta.dataset.totalResults || '';
      this.dataset.pageSize = meta.dataset.pageSize || '';
      this.dataset.currentPage = meta.dataset.currentPage || '';
      this.dataset.pages = meta.dataset.pages || '';

      if (meta.dataset.nextUrl) {
        this.dataset.nextUrl = meta.dataset.nextUrl;
        this.hidden = false;
      } else {
        delete this.dataset.nextUrl;
        this.hidden = true;
      }
    } else {
      // No meta returned — assume no further pages
      delete this.dataset.nextUrl;
      this.hidden = true;
    }
  }
}

if (!customElements.get('load-more-search')) {
  customElements.define('load-more-search', LoadMoreSearch);
}

